import { useState, useEffect, useCallback } from "react";
import { Play, Upload, Trash2, X, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["All", "Windows", "Doors", "Garden Rooms", "Installations", "Testimonials"];

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Installations",
    video_url: "",
    thumbnail_url: ""
  });

  const fetchVideos = useCallback(async () => {
    try {
      const params = selectedCategory !== "All" ? `?category=${selectedCategory}` : "";
      const response = await axios.get(`${API}/videos${params}`);
      setVideos(response.data);
    } catch {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  useEffect(() => {
    // Check if admin is logged in
    const token = sessionStorage.getItem("adminToken");
    setIsAdmin(!!token);
  }, []);

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload MP4, WebM, MOV, or AVI files only");
      return;
    }

    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video file must be less than 100MB");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const token = sessionStorage.getItem("adminToken");
      const response = await axios.post(`${API}/upload/video`, uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setFormData(prev => ({ ...prev, video_url: response.data.url }));
      toast.success("Video uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload video");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const token = sessionStorage.getItem("adminToken");
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      setFormData(prev => ({ ...prev, thumbnail_url: response.data.url }));
      toast.success("Thumbnail uploaded!");
    } catch {
      toast.error("Failed to upload thumbnail");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.video_url) {
      toast.error("Please upload a video first");
      return;
    }

    try {
      const token = sessionStorage.getItem("adminToken");
      await axios.post(`${API}/videos`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Video added successfully!");
      setFormData({
        title: "",
        description: "",
        category: "Installations",
        video_url: "",
        thumbnail_url: ""
      });
      setShowUploadForm(false);
      fetchVideos();
    } catch {
      toast.error("Failed to add video");
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;

    try {
      const token = sessionStorage.getItem("adminToken");
      await axios.delete(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Video deleted");
      fetchVideos();
    } catch {
      toast.error("Failed to delete video");
    }
  };

  const filteredVideos = selectedCategory === "All" 
    ? videos 
    : videos.filter(v => v.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 lg:pt-36">
      <SEO 
        title="Videos | C. Glancey Double Glazing"
        description="Watch our double glazing installation videos. See our quality workmanship on windows, doors, and garden rooms across Lanarkshire."
        keywords="double glazing videos, window installation videos, door installation, Lanarkshire glazing"
      />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-[#1A1A1A] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            OUR <span className="text-[#FF6B00]">VIDEOS</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Watch our installation videos and see the quality of our work across Lanarkshire.
          </p>

          {/* Admin Upload Button */}
          {isAdmin && (
            <button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3 hover:bg-[#E65000] transition-all"
              data-testid="upload-video-btn"
            >
              <Upload className="w-5 h-5" />
              {showUploadForm ? "Close" : "Upload Video"}
            </button>
          )}
        </div>
      </section>

      {/* Upload Form (Admin Only) */}
      {isAdmin && showUploadForm && (
        <section className="py-8 bg-[#1A1A1A]">
          <div className="max-w-2xl mx-auto px-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-white font-medium mb-2">Video Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333] text-white focus:border-[#FF6B00] focus:outline-none"
                  placeholder="e.g. Bay Window Installation in Motherwell"
                  required
                  data-testid="video-title-input"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333] text-white focus:border-[#FF6B00] focus:outline-none"
                  rows={3}
                  placeholder="Brief description of the video..."
                  data-testid="video-description-input"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#333] text-white focus:border-[#FF6B00] focus:outline-none"
                  data-testid="video-category-select"
                >
                  {CATEGORIES.filter(c => c !== "All").map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Upload Video * (Max 100MB)</label>
                <div className="relative">
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                    onChange={handleVideoUpload}
                    className="hidden"
                    id="video-upload"
                    data-testid="video-file-input"
                  />
                  <label
                    htmlFor="video-upload"
                    className={`flex items-center justify-center gap-2 w-full px-4 py-8 border-2 border-dashed ${formData.video_url ? 'border-green-500 bg-green-500/10' : 'border-[#333] hover:border-[#FF6B00]'} cursor-pointer transition-colors`}
                  >
                    {uploading ? (
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-[#FF6B00] animate-spin mx-auto mb-2" />
                        <span className="text-white">Uploading... {uploadProgress}%</span>
                        <div className="w-full bg-[#333] rounded-full h-2 mt-2">
                          <div 
                            className="bg-[#FF6B00] h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    ) : formData.video_url ? (
                      <span className="text-green-400">✓ Video uploaded - Click to replace</span>
                    ) : (
                      <>
                        <Film className="w-8 h-8 text-gray-400" />
                        <span className="text-gray-400">Click to upload video (MP4, WebM, MOV)</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Thumbnail Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                  id="thumbnail-upload"
                  data-testid="thumbnail-file-input"
                />
                <label
                  htmlFor="thumbnail-upload"
                  className={`flex items-center justify-center gap-2 w-full px-4 py-4 border-2 border-dashed ${formData.thumbnail_url ? 'border-green-500 bg-green-500/10' : 'border-[#333] hover:border-[#FF6B00]'} cursor-pointer transition-colors`}
                >
                  {formData.thumbnail_url ? (
                    <span className="text-green-400">✓ Thumbnail uploaded</span>
                  ) : (
                    <span className="text-gray-400">Click to upload thumbnail</span>
                  )}
                </label>
              </div>

              <button
                type="submit"
                disabled={!formData.video_url || !formData.title}
                className="w-full bg-[#FF6B00] text-white font-bold py-4 hover:bg-[#E65000] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-video-btn"
              >
                Add Video
              </button>
            </form>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-8 border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-[#FF6B00] text-white"
                    : "bg-[#1A1A1A] text-gray-400 hover:text-white"
                }`}
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#FF6B00] animate-spin" />
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center py-12">
              <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No videos found in this category.</p>
              {isAdmin && (
                <p className="text-gray-500 mt-2">Click "Upload Video" to add your first video.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map(video => (
                <div
                  key={video.id}
                  className="bg-[#1A1A1A] overflow-hidden group"
                  data-testid={`video-card-${video.id}`}
                >
                  {/* Video Thumbnail / Player */}
                  <div className="relative aspect-video bg-black">
                    {playingVideo === video.id ? (
                      <video
                        src={`${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
                        controls
                        autoPlay
                        className="w-full h-full object-contain"
                        onEnded={() => setPlayingVideo(null)}
                      />
                    ) : (
                      <>
                        {video.thumbnail_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]">
                            <Film className="w-16 h-16 text-[#333]" />
                          </div>
                        )}
                        <button
                          onClick={() => setPlayingVideo(video.id)}
                          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`play-video-${video.id}`}
                        >
                          <div className="w-16 h-16 bg-[#FF6B00] rounded-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-white ml-1" fill="white" />
                          </div>
                        </button>
                      </>
                    )}
                    
                    {/* Admin Delete Button */}
                    {isAdmin && playingVideo !== video.id && (
                      <button
                        onClick={() => handleDelete(video.id)}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                        data-testid={`delete-video-${video.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Video Info */}
                  <div className="p-4">
                    <span className="text-[#FF6B00] text-xs font-medium uppercase tracking-wider">
                      {video.category}
                    </span>
                    <h3 className="text-white font-bold mt-1 line-clamp-2">{video.title}</h3>
                    {video.description && (
                      <p className="text-gray-400 text-sm mt-2 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Modal for larger view */}
      {playingVideo && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#FF6B00] transition-colors"
            onClick={() => setPlayingVideo(null)}
          >
            <X className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default VideosPage;
